// Helpers for pasting into the rich text editors. Content copied from Word,
// another page or a source view often reaches the clipboard as HTML markup,
// and inserting it as plain text shows the tags instead of the formatting.

const MARKUP_TAG = /<\/?(p|div|span|br|hr|h[1-6]|ul|ol|li|a|strong|b|em|i|u|s|strike|table|thead|tbody|tr|th|td|img|blockquote|pre|code|font|center)(\s[^<>]*)?\/?>/i

const ALLOWED_TAGS = new Set([
  'p', 'div', 'span', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'a', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img', 'blockquote', 'pre', 'code'
])

const DROPPED_WITH_CONTENT = new Set([
  'script', 'style', 'meta', 'link', 'title', 'head', 'xml', 'iframe', 'object', 'embed', 'form', 'noscript'
])

const ALLOWED_ATTRIBUTES = {
  a: ['href', 'title'],
  img: ['src', 'alt', 'width', 'height'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan']
}

const SAFE_URL = /^(https?:|mailto:|tel:|#|\/)/i

export function looksLikeHtml(text) {
  return typeof text === 'string' && MARKUP_TAG.test(text)
}

function cleanNode(node, doc) {
  if (node.nodeType === 3) {
    return doc.createTextNode(node.nodeValue)
  }
  if (node.nodeType !== 1) {
    return null
  }

  const tag = node.tagName.toLowerCase()
  if (DROPPED_WITH_CONTENT.has(tag)) {
    return null
  }

  const fragment = doc.createDocumentFragment()
  node.childNodes.forEach(child => {
    const cleaned = cleanNode(child, doc)
    if (cleaned) fragment.appendChild(cleaned)
  })

  if (!ALLOWED_TAGS.has(tag)) {
    return fragment
  }

  const el = doc.createElement(tag)
  ;(ALLOWED_ATTRIBUTES[tag] || []).forEach(name => {
    const value = node.getAttribute(name)
    if (value === null) return
    if ((name === 'href' || name === 'src') && !SAFE_URL.test(value.trim())) return
    el.setAttribute(name, value)
  })
  if (tag === 'a' && el.getAttribute('href')) {
    el.setAttribute('target', '_blank')
    el.setAttribute('rel', 'noopener noreferrer')
  }
  el.appendChild(fragment)
  return el
}

// Reduces pasted HTML to formatting only: no scripts, styles, classes, Word
// (mso) properties, event handlers or unsafe URLs.
export function sanitizePastedHtml(html) {
  const doc = new DOMParser().parseFromString(html || '', 'text/html')
  const out = doc.createElement('div')
  doc.body.childNodes.forEach(child => {
    const cleaned = cleanNode(child, doc)
    if (cleaned) out.appendChild(cleaned)
  })
  return out.innerHTML.trim()
}

// Returns the HTML to insert for a paste event, or null when the clipboard
// holds plain text that should be inserted as is.
export function htmlFromClipboard(clipboardData) {
  if (!clipboardData) return null

  const plain = clipboardData.getData('text/plain')
  // Plain text that is itself markup (for example copied from a source view,
  // or pasted with "paste and match style") is rendered, not shown as tags.
  if (looksLikeHtml(plain)) {
    return sanitizePastedHtml(plain)
  }

  const html = clipboardData.getData('text/html')
  if (html) {
    const cleaned = sanitizePastedHtml(html)
    if (cleaned) return cleaned
  }

  return null
}
